import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import { isLocalStorageAvailable } from '../app/utils/storage'
import en from './locales/en.json'

// Each language is shown in its own name, so people can find theirs whatever language is active.
export const LANGUAGES = [
    { code: 'en', name: 'English' },
    { code: 'nl', name: 'Nederlands' },
    { code: 'fr', name: 'Français' },
] as const

export type Language = typeof LANGUAGES[number]['code']

export const DEFAULT_LANGUAGE: Language = 'en'

// Only English is bundled. Other languages are separate chunks, loaded when someone picks them.
const loaders: Record<Exclude<Language, typeof DEFAULT_LANGUAGE>, () => Promise<{ default: typeof en }>> = {
    nl: () => import('./locales/nl.json'),
    fr: () => import('./locales/fr.json'),
}

// Not 'language': the Sigma report already stores its own language under that key.
const STORAGE_KEY = 'uiLanguage'

const isLanguage = (value: unknown): value is Language => LANGUAGES.some((language) => language.code === value)

export function getStoredLanguage(): Language {
    if (!isLocalStorageAvailable()) {
        return DEFAULT_LANGUAGE
    }
    const stored = localStorage.getItem(STORAGE_KEY)
    return isLanguage(stored) ? stored : DEFAULT_LANGUAGE
}

async function loadLanguage(language: Language): Promise<void> {
    if (language === DEFAULT_LANGUAGE || i18n.hasResourceBundle(language, 'translation')) {
        return
    }
    const { default: resources } = await loaders[language]()
    i18n.addResourceBundle(language, 'translation', resources)
}

// Resources are bundled, so init runs synchronously: strings are ready before the first render
// and in module code that runs at import time.
i18n.use(initReactI18next).init({
    resources: { en: { translation: en } },
    lng: DEFAULT_LANGUAGE,
    fallbackLng: DEFAULT_LANGUAGE,
    initAsync: false,
    // React already escapes rendered text.
    interpolation: { escapeValue: false },
})

i18n.on('languageChanged', (language) => {
    document.documentElement.lang = language
})
document.documentElement.lang = i18n.language

// Call before the first render. If the saved language cannot load, the app stays in English.
export async function initLanguage(): Promise<void> {
    const language = getStoredLanguage()
    if (language === DEFAULT_LANGUAGE) {
        return
    }
    try {
        await loadLanguage(language)
        await i18n.changeLanguage(language)
    } catch {
        await i18n.changeLanguage(DEFAULT_LANGUAGE)
    }
}

// Many class components and helpers call t() directly and would not re-render, so a switch reloads
// the page: everything then renders in one language. Without storage a reload would lose the choice,
// so the language changes in place instead.
export async function switchLanguage(language: Language): Promise<void> {
    await loadLanguage(language)
    if (!isLocalStorageAvailable()) {
        await i18n.changeLanguage(language)
        return
    }
    localStorage.setItem(STORAGE_KEY, language)
    window.location.reload()
}

export { t } from 'i18next'
export { useTranslation, Trans } from 'react-i18next'
export default i18n
